import React, { useState, useEffect, useCallback } from 'react'
import { Search, BarChart3, Users, TrendingUp, DollarSign, Download, ChevronDown, ChevronUp, Filter, X } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext'
import { apiService } from '../../services/apiService'

const MemberProductProfiles = ({ isSuperAdmin = false }) => {
  const { admin } = useAdminAuth()
  const { superAdmin } = useSuperAdminAuth()

  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [searchProduct, setSearchProduct] = useState('')
  const [periodFilter, setPeriodFilter] = useState('')
  const [expandedProduct, setExpandedProduct] = useState(null)

  // Product report modal
  const [reportProduct, setReportProduct] = useState(null)
  const [reportData, setReportData] = useState(null)
  const [loadingReport, setLoadingReport] = useState(false)

  const [meta, setMeta] = useState({ total_products: 0, total_statements_processed: 0 })

  const fetchProfiles = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await apiService.getMemberProductProfiles({
        productName: searchProduct,
        reportingPeriod: periodFilter,
        isSuperAdmin
      })
      if (res.status === 'SUCCESS') {
        setProducts(res.data.products || [])
        setMeta({
          total_products: res.data.total_products || 0,
          total_statements_processed: res.data.total_statements_processed || 0
        })
      } else {
        setError(res.message || 'Failed to load profiles')
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [searchProduct, periodFilter, isSuperAdmin])

  useEffect(() => {
    fetchProfiles()
  }, [fetchProfiles])

  const openReport = async (productName) => {
    setReportProduct(productName)
    setLoadingReport(true)
    setReportData(null)
    try {
      const res = await apiService.getProductReport(productName, periodFilter, isSuperAdmin)
      if (res.status === 'SUCCESS') {
        setReportData(res.data)
      }
    } catch (e) {
      console.error('Report error:', e)
    } finally {
      setLoadingReport(false)
    }
  }

  const exportCSV = (productEntry) => {
    const rows = [
      ['Member No', 'Member Name', 'Period', 'Revenue (UGX)', 'COGS (UGX)', 'Gross Profit (UGX)', 'Notes'],
      ...productEntry.members.map(m => [
        m.mem_no, m.customer_name, m.reporting_period,
        m.revenue, m.cost_of_goods_sold, m.gross_profit, m.notes || ''
      ])
    ]
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${productEntry.product_name}_member_report.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const fmt = (n) => new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX', maximumFractionDigits: 0 }).format(n || 0)

  const handleSearch = (e) => {
    e.preventDefault()
    fetchProfiles()
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <BarChart3 className="h-8 w-8 text-blue-600" />
          Member Product Profiles
        </h1>
        <p className="text-gray-600 mt-1">
          {isSuperAdmin
            ? 'View and analyse products posted by members across all MGs in their financial statements'
            : `View and analyse products posted by members in ${admin?.mgCode || 'your MG'}`}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search Product</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchProduct}
                onChange={e => setSearchProduct(e.target.value)}
                placeholder="e.g. Maize, Beans..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Period</label>
            <input
              type="text"
              value={periodFilter}
              onChange={e => setPeriodFilter(e.target.value)}
              placeholder="e.g. 2024-Q1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            type="submit"
            className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 font-medium"
          >
            <Filter className="h-4 w-4" />
            Apply
          </button>
          {(searchProduct || periodFilter) && (
            <button
              type="button"
              onClick={() => { setSearchProduct(''); setPeriodFilter('') }}
              className="flex items-center gap-2 text-gray-600 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50"
            >
              <X className="h-4 w-4" /> Clear
            </button>
          )}
        </form>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
          <div className="bg-blue-100 rounded-full p-3"><BarChart3 className="h-6 w-6 text-blue-600" /></div>
          <div>
            <p className="text-sm text-gray-500">Unique Products</p>
            <p className="text-2xl font-bold text-gray-900">{meta.total_products}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
          <div className="bg-green-100 rounded-full p-3"><Users className="h-6 w-6 text-green-600" /></div>
          <div>
            <p className="text-sm text-gray-500">Statements Processed</p>
            <p className="text-2xl font-bold text-gray-900">{meta.total_statements_processed}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
          <div className="bg-purple-100 rounded-full p-3"><TrendingUp className="h-6 w-6 text-purple-600" /></div>
          <div>
            <p className="text-sm text-gray-500">Total Revenue (all products)</p>
            <p className="text-2xl font-bold text-gray-900">
              {fmt(products.reduce((s, p) => s + p.total_revenue, 0))}
            </p>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-red-700">{error}</div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
        </div>
      )}

      {/* Product List */}
      {!loading && products.length === 0 && !error && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center text-gray-500">
          <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-lg font-medium">No products found</p>
          <p className="text-sm">Try adjusting your search or clearing filters</p>
        </div>
      )}

      {!loading && products.length > 0 && (
        <div className="space-y-3">
          {products.map((product, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Product header row */}
              <div
                className="flex flex-wrap items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedProduct(expandedProduct === idx ? null : idx)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="bg-blue-100 rounded-full w-10 h-10 flex items-center justify-center font-bold text-blue-700">
                    {product.product_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-lg">{product.product_name}</p>
                    <p className="text-sm text-gray-500">{product.member_count} member{product.member_count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-6 items-center">
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Total Revenue</p>
                    <p className="font-semibold text-green-700">{fmt(product.total_revenue)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Gross Profit</p>
                    <p className="font-semibold text-blue-700">{fmt(product.total_gross_profit)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={e => { e.stopPropagation(); openReport(product.product_name) }}
                      className="flex items-center gap-1 text-sm text-blue-600 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50"
                    >
                      <TrendingUp className="h-3.5 w-3.5" /> Report
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); exportCSV(product) }}
                      className="flex items-center gap-1 text-sm text-gray-600 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50"
                    >
                      <Download className="h-3.5 w-3.5" /> CSV
                    </button>
                    {expandedProduct === idx
                      ? <ChevronUp className="h-5 w-5 text-gray-400" />
                      : <ChevronDown className="h-5 w-5 text-gray-400" />}
                  </div>
                </div>
              </div>

              {/* Member list (expanded) */}
              {expandedProduct === idx && (
                <div className="border-t border-gray-100 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                      <tr>
                        <th className="px-4 py-2 text-left">Member</th>
                        <th className="px-4 py-2 text-left">Period</th>
                        <th className="px-4 py-2 text-right">Revenue</th>
                        <th className="px-4 py-2 text-right">COGS</th>
                        <th className="px-4 py-2 text-right">Gross Profit</th>
                        <th className="px-4 py-2 text-left">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.members.map((m, mi) => (
                        <tr key={mi} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <p className="font-medium text-gray-900">{m.customer_name}</p>
                            <p className="text-xs text-gray-400">{m.mem_no}</p>
                          </td>
                          <td className="px-4 py-2 text-gray-600">{m.reporting_period || '—'}</td>
                          <td className="px-4 py-2 text-right text-green-700 font-medium">{fmt(m.revenue)}</td>
                          <td className="px-4 py-2 text-right text-red-600">{fmt(m.cost_of_goods_sold)}</td>
                          <td className="px-4 py-2 text-right text-blue-700 font-medium">{fmt(m.gross_profit)}</td>
                          <td className="px-4 py-2 text-gray-500 text-xs max-w-xs truncate">{m.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 font-semibold">
                      <tr>
                        <td colSpan={2} className="px-4 py-2 text-gray-700">Totals</td>
                        <td className="px-4 py-2 text-right text-green-700">{fmt(product.total_revenue)}</td>
                        <td className="px-4 py-2 text-right text-red-600">{fmt(product.total_cogs)}</td>
                        <td className="px-4 py-2 text-right text-blue-700">{fmt(product.total_gross_profit)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Product Report Modal */}
      {reportProduct && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Product Report: {reportProduct}
              </h2>
              <button onClick={() => { setReportProduct(null); setReportData(null) }}
                className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {loadingReport && (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
                </div>
              )}
              {reportData && (
                <div className="space-y-5">
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'Members', value: reportData.member_count, color: 'blue' },
                      { label: 'Total Revenue', value: fmt(reportData.total_revenue), color: 'green' },
                      { label: 'Total Gross Profit', value: fmt(reportData.total_gross_profit), color: 'purple' },
                      { label: 'Avg Revenue', value: fmt(reportData.average_revenue), color: 'orange' }
                    ].map(card => (
                      <div key={card.label} className={`bg-${card.color}-50 border border-${card.color}-200 rounded-lg p-3`}>
                        <p className="text-xs text-gray-500">{card.label}</p>
                        <p className={`text-lg font-bold text-${card.color}-700`}>{card.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Period Trends */}
                  {reportData.period_trends.length > 1 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Period Trends</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                            <tr>
                              <th className="px-3 py-2 text-left">Period</th>
                              <th className="px-3 py-2 text-right">Members</th>
                              <th className="px-3 py-2 text-right">Revenue</th>
                              <th className="px-3 py-2 text-right">Gross Profit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.period_trends.map((t, i) => (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="px-3 py-2">{t.period}</td>
                                <td className="px-3 py-2 text-right">{t.member_count}</td>
                                <td className="px-3 py-2 text-right text-green-700">{fmt(t.total_revenue)}</td>
                                <td className="px-3 py-2 text-right text-blue-700">{fmt(t.total_gross_profit)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Members table */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Member Breakdown</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                          <tr>
                            <th className="px-3 py-2 text-left">Member</th>
                            <th className="px-3 py-2 text-left">Period</th>
                            <th className="px-3 py-2 text-right">Revenue</th>
                            <th className="px-3 py-2 text-right">Gross Profit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.members.map((m, i) => (
                            <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                              <td className="px-3 py-2">
                                <p className="font-medium">{m.customer_name}</p>
                                <p className="text-xs text-gray-400">{m.mem_no}</p>
                              </td>
                              <td className="px-3 py-2 text-gray-600">{m.reporting_period}</td>
                              <td className="px-3 py-2 text-right text-green-700">{fmt(m.revenue)}</td>
                              <td className="px-3 py-2 text-right text-blue-700">{fmt(m.gross_profit)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MemberProductProfiles
